import { createFileRoute, Outlet, redirect, useChildMatches } from '@tanstack/react-router'
import { ExpenseList } from '@/features/hr-expenses/expense-list'
import { hasGrant } from '@/lib/me'

/**
 * Route shell for the expenses screen, following
 * `routes/_authenticated/users.tsx`.
 *
 * The route sits under `_authenticated` so the layout's `beforeLoad` guard has
 * resolved the session before the screen mounts.
 *
 * The grant guard is presentation only. The API enforces `expense.view` on
 * every claim endpoint and the module gate refuses the whole `/api/hr` prefix
 * for a group whose plan does not include HR, so a stale grant still 403s
 * server-side; this only keeps a user without the grant out of a screen they
 * cannot use.
 *
 * Per the route-shell rule in `apps/company/AGENTS.md`, this file is a route
 * definition, a guard and a render. The screen lives in
 * `src/features/hr-expenses/`.
 *
 * ## Why this renders an Outlet
 *
 * `expenses.$claimId.tsx` is a CHILD of this route in flat file routing, so without an
 * Outlet it never mounts and the detail URL silently rendered this list
 * instead. Slice 8 found the same shape in three places; see
 * `employees.$employeeId.tsx` for the full note and the tidier fix.
 */
export const Route = createFileRoute('/_authenticated/hr/expenses')({
  beforeLoad: ({ context }) => {
    if (!hasGrant(context.queryClient, 'expense.view')) {
      throw redirect({ to: '/' })
    }
  },
  component: HrExpensesRoute,
})

function HrExpensesRoute() {
  const children = useChildMatches()

  if (children.length > 0) {
    return <Outlet />
  }

  return <ExpenseList />
}
